import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Preview as PreviewIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import FileRenderer from './FileRenderer';
import FileUploader from './FileUploader';
import { useFileManager } from '../../hooks/useFileManager';
import { useFileContent } from '../../hooks/useFileContent';
import { MAX_FILE_SIZE } from '../../constants/fileConstants';

/**
 * FileViewer - Enhanced file viewer with optional management capabilities
 * 
 * This component provides file viewing with optional upload, preview, and removal
 * capabilities. It integrates file content loading and management seamlessly.
 * 
 * @param {object} props
 * @param {Array} props.files - Array of file objects (must have id, name/originalName, mimeType)
 * @param {number} props.selectedFileIndex - Index of selected file 
 * @param {boolean} props.showPreview - Whether preview panel is visible
 * @param {function} props.onFileChange - Function called when file tab changes
 * @param {object} props.customRenderers - Additional renderers for FileRenderer
 * @param {object} props.customActions - Additional actions for FileRenderer
 * @param {object} props.customPatterns - Additional MIME type patterns
 * @param {string} props.title - Custom title for the preview panel
 * @param {object} props.sx - Additional styles for the container
 * 
 * // Management props (optional)
 * @param {FileService} props.fileService - File service instance (enables management)
 * @param {Array} props.initialFiles - Initial files list (for management mode) // unused
 * @param {function} props.onFilesChanged - Callback when files list changes
 * @param {number} props.maxFileSize - Maximum file size in bytes
 * @param {Array<string>} props.allowedMimeTypes - Allowed MIME types
 * @param {boolean} props.readOnly - Whether file management is read-only
 * @param {boolean} props.enableManagement - Whether to enable file management features
 */
const FileViewer = ({ 
  files: externalFiles, 
  selectedFileIndex: externalSelectedIndex, 
  showPreview = true, 
  onFileChange: externalOnFileChange,
  customRenderers = {},
  customActions = null,
  customPatterns = {},
  title = "File Preview",
  sx = {},
  enableInteractivePreview = false,
  
  // Management props
  fileService = null,
  initialFiles = [],
  onFilesChanged = null,
  maxFileSize = MAX_FILE_SIZE,
  allowedMimeTypes = null,
  readOnly = false,
  enableManagement = false
}) => {
  // Management state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  // Determine if we're in management mode
  const isManagementMode = enableManagement && fileService;
  
  // File management hook (only if in management mode)
  const fileManager = useFileManager({
    fileService,
    onFilesChanged,
    maxFileSize,
    allowedMimeTypes
  });

  /* ---------------------------------------------------------------------
   * Sync external files only once when the component is mounted.
   * Afterwards, the internal FileManager state becomes the source of truth
   * for the list to avoid re-adding files that have just been deleted locally.
   * ------------------------------------------------------------------- */
  const didInitFilesRef = useRef(false);
  useEffect(() => {
    if (isManagementMode && !didInitFilesRef.current) {
      if (initialFiles?.length > 0) {
        fileManager.setFilesList(initialFiles);
        didInitFilesRef.current = true;
      } else if (externalFiles?.length > 0) {
        fileManager.setFilesList(externalFiles);
        didInitFilesRef.current = true;
      }
    }
  }, [isManagementMode, initialFiles, externalFiles, fileManager]);

  // Use management files or external files
  // Once initialised, FileManager keeps its own copy of the list.
  const files = isManagementMode ? fileManager.files : (externalFiles || []);
  
  // Use external selectedFileIndex as single source of truth
  const selectedFileIndex = externalSelectedIndex || 0;

  /* ---------------------------------------------------------------------
   * Keep the selected tab index within the bounds of the current files list.
   * – If there are no files, deselect the tab with `false`.
   * – If the current index exceeds the new length (e.g., after a deletion),
   *   snap to the last available index.
   * ------------------------------------------------------------------- */
  useEffect(() => {
    if (files.length === 0 && selectedFileIndex !== 0) {
      // Notify parent to reset index
      if (externalOnFileChange) {
        externalOnFileChange(null, 0);
      }
    } else if (files.length > 0 && selectedFileIndex >= files.length) {
      // Notify parent to adjust index
      if (externalOnFileChange) {
        externalOnFileChange(null, files.length - 1);
      }
    }
  }, [files.length, selectedFileIndex, externalOnFileChange]);

  // File content hook (always call it, but only use it when we have files and service)
  const fileContentHook = useFileContent({ 
    fileService: files.length > 0 && fileService ? fileService : null 
  });

  // Handle file selection from uploader
  const handleFileSelect = useCallback(async (selectedFiles) => {
    if (!isManagementMode) return;
    
    try {
      fileManager.clearErrors();
      
      for (const file of selectedFiles) {
        await fileManager.addFile(file);
      }
      externalOnFileChange(null, files.length);
      //close the dialog
      setShowUploadDialog(false);
    } catch (error) {
      console.error('Error uploading files:', error);
    }
  }, [isManagementMode, fileManager]);

  // Handle file removal
  const handleDeleteFile = useCallback(async () => {
    if (!fileToDelete || !isManagementMode) return;
    
    try {
      await fileManager.deleteFile(fileToDelete.id);
      setShowDeleteDialog(false);
      setFileToDelete(null);
      // Adjust selected index if needed
      if (selectedFileIndex > 0) {
        // Notify parent to adjust index
        if (externalOnFileChange) {
          externalOnFileChange(null, selectedFileIndex - 1);
        }
      }
    } catch (error) {
      console.error('Error removing file:', error);
    }
  }, [fileManager, fileToDelete, isManagementMode, files.length, selectedFileIndex, externalOnFileChange]);

  // Handle file tab change
  const handleFileChange = useCallback((event, newValue) => {
    // Validate that this is a legitimate tab change event
    if (!event || typeof newValue !== 'number' || newValue < 0 || newValue >= files.length) {
      return;
    }
    
    // Notify parent component of the change
    if (externalOnFileChange) {
      externalOnFileChange(event, newValue);
    }
  }, [externalOnFileChange, files.length]);

  // Handle delete click
  const handleDeleteClick = useCallback((file, event) => {
    event.stopPropagation();
    setFileToDelete(file);
    setShowDeleteDialog(true);
  }, []);

  // Get current file and content
  const selectedFile = files[selectedFileIndex];
  const fileContent = fileContentHook && selectedFile?.id ? fileContentHook.getCachedContent(selectedFile.id) : null;
  const fileLoading = fileContentHook && selectedFile?.id ? fileContentHook.isLoading(selectedFile.id) : false;
  const fileError = fileContentHook && selectedFile?.id ? fileContentHook.getError(selectedFile.id) : null;

  // Load file content for selected file only with circuit breaker protection
  useEffect(() => {
    if (fileContentHook && selectedFile?.id) {
      // Circuit Breaker: Respect permanent failures to prevent infinite loops
      if (!fileContentHook.isPermanentlyFailed(selectedFile.id)) {
        fileContentHook.loadFileContent(selectedFile.id).catch(error => {
          // Silently handle errors to prevent render loops
          // Error state is already managed by the useFileContent hook
          console.debug(`File loading failed for ${selectedFile.id}:`, error.message);
        });
      }
    }
  }, [fileContentHook, selectedFile?.id]);

  // Removed aggressive preloading - files will load on-demand when selected
  // This eliminates the 3x duplicate calls your boss observed

  return (
    <Box
      sx={{
        ...sx,
        height: '100%',
        width: '100%',
        display: showPreview ? 'flex' : 'none',
        flexDirection: 'column',
        transform: showPreview ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s ease'
      }}
    >
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2,
        flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PreviewIcon fontSize="small" />
          <Typography variant="h6">{title}</Typography>
          <Chip 
            label={`${files.length} file${files.length !== 1 ? 's' : ''}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>

        {isManagementMode && !readOnly && (
          <Tooltip title="Add files">
            <IconButton 
              onClick={() => setShowUploadDialog(true)}
              color="primary"
              size="small"
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* File viewer */}
      <Box sx={{
        flexGrow: 1,
        position: 'relative',
        overflow: 'hidden',
        minHeight: 0,
        height: '100%',
        maxHeight: '100%',
      }}>
        <Paper sx={{
          height: '100%',
          maxHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          willChange: 'auto'
        }}>
        {/* File tabs */}
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0}}>
          <Tabs
            value={files.length > 0 ? selectedFileIndex : false}
            onChange={handleFileChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              flexShrink: 0,
              minHeight: '48px',
              '& .MuiTab-root': {
                // Ensure tabs don't interfere with navigation
                position: 'relative',
                zIndex: 1,
                minHeight: '48px'
              },
              '& .MuiTabs-scrollButtons': {
                // Prevent layout shift when scroll buttons appear/disappear
                width: 'auto',
                '&.Mui-disabled': {
                  opacity: 0,
                  width: 0
                }
              }
            }}
          >
            {files.map((file, index) => (
              <Tab 
                key={file.id} 
                label={file.name || file.originalName}
                value={index}
              />
            ))}
          </Tabs>

            {/* File content preview */}
            <Box sx={{ flexGrow: 1, overflow: 'auto', minHeight: 0}}>
            {selectedFile ? (
              <FileRenderer
                file={selectedFile}
                content={fileContent}
                loading={fileLoading}
                error={fileError}
                onReloadContent={(fileId) => fileContentHook?.loadFileContent(fileId, true)}
                customRenderers={{
                  ...customRenderers,
                  // This overrides the default renderer in case the file is valid.
                  default: ({ file }) => {
                    const isPermanentlyFailed = fileContentHook?.isPermanentlyFailed(file.id);
                    const retryCount = fileContentHook?.getRetryCount(file.id) || 0;
                    
                    return (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        p: 5,
                        height: '100%',
                        position: 'relative'
                      }}>
                        <Typography variant="h6" gutterBottom>
                          {file.name || file.originalName}
                        </Typography>
                        {file.size && (
                          <Typography color="text.secondary" gutterBottom>
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </Typography>
                        )}
                        <Typography color="text.secondary" gutterBottom>
                          {file.mimeType}
                        </Typography>
                        {isPermanentlyFailed && (
                          <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Typography color="error" variant="body2" gutterBottom>
                              File failed to load after {retryCount} attempts
                            </Typography>
                            <Button 
                              size="small" 
                              onClick={() => fileContentHook?.resetCircuitBreaker(file.id)}
                              variant="outlined"
                              color="primary"
                            >
                              Retry Loading
                            </Button>
                          </Box>
                        )}
                      </Box>
                    );
                  }
                }}
                customActions={customActions? customActions : ({file}) => (
                  <Box>
                    {/* Action buttons */}
                    {isManagementMode && !readOnly && (
                          <Tooltip title="Delete file">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => handleDeleteClick(file, e)}
                              disabled={fileManager?.isLoading(`remove-${file.id}`)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                      )}
                  </Box>
                )}
                customPatterns={customPatterns}
                interactivePreview={enableInteractivePreview}
              />
            ) : (
              <Box sx={{ 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                p: 2
              }}>
                <Typography color="text.secondary">No files to display</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
      </Box>

      {/* Upload Dialog */}
      {isManagementMode && (
        <Dialog 
          open={showUploadDialog} 
          onClose={() => setShowUploadDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Upload Files
            <IconButton
              onClick={() => setShowUploadDialog(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <FileUploader
              onFileSelect={handleFileSelect}
              loading={fileManager?.isLoading('upload')}
              error={fileManager?.getError('upload')}
              maxFileSize={maxFileSize}
              allowedMimeTypes={allowedMimeTypes}
              multiple={true}
              title="Upload Files"
              sx={{ mt: 1 }}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {isManagementMode && (
        <Dialog 
          open={showDeleteDialog} 
          onClose={() => setShowDeleteDialog(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{fileToDelete?.name || fileToDelete?.originalName}"?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteFile}
              color="error"
              disabled={fileManager?.isLoading(`remove-${fileToDelete?.id}`)}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default FileViewer; 
